import * as yup from "yup";
import { generateText, getSchema } from "@tiptap/core";
import { TEXT_EXTRACTION_EXTENSIONS } from "./tiptapSchemaProfiles";
import { stripUnknownMarks } from "./stripUnknownMarks";

export const MAX_NUM_CHARACTERS_PER_FIELD = 50_000; // characters here means text, not fictional entities

let textExtractionSchema = null;

function getTextExtractionSchema() {
  if (!textExtractionSchema) {
    textExtractionSchema = getSchema(TEXT_EXTRACTION_EXTENSIONS);
  }
  return textExtractionSchema;
}

function pruneEmptyTextNodes(node) {
  if (Array.isArray(node)) {
    return node.map(pruneEmptyTextNodes).filter(Boolean);
  }

  if (!node || typeof node !== "object") {
    return node;
  }

  if (node.type === "text" && !node.text) {
    return null;
  }

  const result = { ...node };
  if (Array.isArray(result.content)) {
    const content = result.content.map(pruneEmptyTextNodes).filter(Boolean);
    if (content.length > 0) {
      result.content = content;
    } else {
      delete result.content;
    }
  }

  return result;
}

export function extractTextFromTipTap(json) {
  if (!json) return "";
  const sanitizedJson = pruneEmptyTextNodes(json);
  if (!sanitizedJson) return "";

  try {
    return generateText(sanitizedJson, TEXT_EXTRACTION_EXTENSIONS);
  } catch {
    try {
      return generateText(
        stripUnknownMarks(sanitizedJson, getTextExtractionSchema()),
        TEXT_EXTRACTION_EXTENSIONS,
      );
    } catch {
      return "";
    }
  }
}

export const BLANK_TIPTAP_JSON = {
  type: "doc",
  content: [
    {
      type: "paragraph",
    },
  ],
};

export const BLANK_HTML = "<p></p>";

function tipTapFieldWithinLimit(value) {
  try {
    if (typeof value === "string") {
      const htmlRegex = /<[^>]+>/g;
      try {
        const textWithoutHtml = value.replace(htmlRegex, "");
        return textWithoutHtml.length <= MAX_NUM_CHARACTERS_PER_FIELD;
      } catch (e) {
        return value.length <= MAX_NUM_CHARACTERS_PER_FIELD;
      }
    }
    const text = extractTextFromTipTap(value);
    return text.length <= MAX_NUM_CHARACTERS_PER_FIELD;
  } catch (e) {
    return true;
  }
}

// accepts either TipTap JSON object or HTML string (when ?format=html is used)
// for JSON: does it have a type and content? does every object in content have a type?
// note that this does not check deeply nested objects, just the first layer of nodes
export const TipTapFieldSchema = yup
  .mixed()
  .test("is-valid-tiptap-field", "Invalid TipTap JSON", (value) => {
    try {
      if (typeof value === "string") {
        if (value.trim() === "") return true;
        const htmlTagRegex = /<[^>]+>/;
        return htmlTagRegex.test(value);
      }
      if (value.type && Array.isArray(value.content)) {
        return value.content.every((item) => typeof item === "object" && item.type);
      }
      return false;
    } catch (e) {
      return false;
    }
  })
  .test("max-characters", `Text exceeds maximum character limit of ${MAX_NUM_CHARACTERS_PER_FIELD}`, (value) => {
    return tipTapFieldWithinLimit(value);
  });

export const PlainTextFieldSchema = yup
  .string()
  .max(MAX_NUM_CHARACTERS_PER_FIELD)
  .default("");
